import { useEffect, useMemo, useState } from "react";
import { Bell, X } from "lucide-react";
import { initClientSocket } from "../socketClient";
import api from "../axiosConfig";

export default function InAppNotifications({ phone }) {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);

  const unread = useMemo(
    () => notifs.filter((n) => !n.read).length,
    [notifs]
  );

  useEffect(() => {
    if (!phone) return;

    const targetKey = `client:${String(phone).trim()}`;

    // 1️⃣ Charger l’historique depuis la DB
    const fetchNotifs = async () => {
      try {
        const res = await api.get("/notifications", {
          params: { targetKey, limit: 50 },
        });
        setNotifs(res.data.notifications || []);
      } catch {
        // silencieux (UX first)
      }
    };

    fetchNotifs();

    // 2️⃣ Temps réel (socket)
    const socket = initClientSocket(phone);

    const onNotification = (payload) => {
      setNotifs((prev) => {
        if (payload?._id && prev.some((n) => n._id === payload._id)) {
          return prev;
        }
        return [{ ...payload }, ...prev].slice(0, 50);
      });
    };

    socket.on("notification", onNotification);

    return () => {
      socket.off("merchant_notification", onNotification);    
    };
  }, [phone]);

  // ✅ Tout lire = DB + UI
  const markAllRead = async () => {
    if (!phone) return;
    const targetKey = `client:${String(phone).trim()}`;

    try {
      await api.put("/notifications/read-all", { targetKey });
    } catch {
      // même si l’API échoue, on ne casse pas l’UI
    }

    setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  // 🧼 Effacer = UI uniquement (historique DB conservé)
  const clearAll = () => {
    setNotifs([]);
  };

  return (
    <div className="relative">
      {/* 🔔 Bell */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-xl hover:bg-gray-100"
        title="Notifications"
      >
        <Bell size={20} className="text-gray-700" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-extrabold text-white bg-[#f34141] rounded-full flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>

      {/* 📩 Panel */}
      {open && (
        <div className="absolute right-0 mt-3 w-[360px] bg-white border border-gray-200 shadow-xl rounded-2xl overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="font-bold text-gray-800">Notifications</div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded-lg hover:bg-gray-100"
            >
              <X size={18} className="text-gray-500" />
            </button>
          </div>

          <div className="flex gap-2 px-4 py-2 border-b bg-gray-50">
            <button
              onClick={markAllRead}
              className="px-3 py-1 text-xs font-bold bg-white border rounded-lg hover:bg-gray-100"
            >
              Tout lire
            </button>
            <button
              onClick={clearAll}
              className="px-3 py-1 text-xs font-bold bg-white border rounded-lg hover:bg-gray-100"
            >
              Effacer
            </button>
          </div>

          <div className="max-h-[340px] overflow-y-auto">
            {notifs.length === 0 ? (
              <div className="p-6 text-sm text-center text-gray-400">
                Aucune notification.
              </div>
            ) : (
              notifs.map((n) => (
                <div
                  key={n._id}
                  className={`px-4 py-3 border-b ${
                    n.read ? "bg-white" : "bg-red-50/30"
                  }`}
                >
                  <div className="text-sm font-extrabold text-gray-800">
                    {n.title}
                  </div>
                  <div className="mt-1 text-xs text-gray-600">
                    {n.body}
                  </div>
                  {n.data?.orderNumber && (
                    <div className="text-[11px] font-mono text-gray-400 mt-2">
                      #{n.data.orderNumber}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}